/**
 * Shapes of the per-media "story" data files served from the preview CDN
 * (`/{libraryId}/{mediaId}/data/<file>.json`) and read with `useMediaData`.
 *
 * Nothing here is guaranteed. A file 404s until the pipeline stage that writes it has run,
 * and every field inside one is optional — so consumers use optional chaining and treat a
 * missing file as "not produced", never as an error.
 *
 * Both files carry `worker_version`, the pipeline worker that wrote them (`"1.1.3"` as of
 * writing). It is declared rather than dropped because these files sit on the CDN for the life
 * of the media: after a pipeline change, already-processed media keep their old file while new
 * media get the new one, and the stamp is the only thing in the bytes that says which shape a
 * reader is holding. Nothing reads it yet, and nothing should start without agreeing with the
 * pipeline owner what an unknown version means — media processed before the stamp existed
 * carry none, so its absence has to stay non-fatal.
 */

export type Scene = {
  scene_id: number;
  title?: string;
  /** Seconds from the start of the media. */
  start?: number;
  end?: number;
  summary?: string;
};

export type ScenesFile = {
  worker_version?: string;
  scenes?: Scene[];
};

export type NarrativeThread = {
  thread_id: number;
  title?: string;
  /** Scene ids (matching `Scene.scene_id`) this thread runs through. */
  scenes_included?: number[];
  /** Prose summary; contains inline `[scene_N]` references. */
  summary?: string;
};

export type NarrativeThreadsFile = {
  worker_version?: string;
  threads?: NarrativeThread[];
};

/**
 * Palette for narrative threads. Assigned by the thread's position in the file (stable across
 * views); cycles past twenty. Shared, so one thread wears one colour on every surface.
 *
 * Hues stay in one analogous family around violet — indigo (h 270) through violet to rose
 * (h 350) — with white and grey neutrals. Lightness runs L .52–.97 and chroma C .01–.25, so
 * identity rides on three axes, not just hue.
 *
 * **The grouping is the point, and it is not decorative.** Twenty colours cannot all be told
 * apart inside one hue family: the best 20-colour set measured a worst pair of ΔE 5.7, far
 * under the 15 a reader needs, and every thread renders at once as a lane, so any two can sit
 * side by side. So the twenty are built as **four groups of five**, where the five inside a
 * group are mutually distinct (worst pair ΔE 15.7) and each group is drawn with a different
 * secondary channel — see `threadVariant`. Two threads are then separated by colour when they
 * share a channel, and by the channel itself when they do not.
 *
 * Consequences for anyone editing this array:
 *
 * - **Order is structural.** `threadVariant` derives the channel from the index by integer
 *   division, so slots 0–4 are group one, 5–9 group two, and so on. Moving a colour between
 *   groups breaks the guarantee that a group's five are mutually distinct.
 * - Adding or removing a colour must keep the length a multiple of `COLOURS_PER_VARIANT`.
 * - The first five are the best-separated group, so the common case — a media with a handful
 *   of threads — is carried by colour alone before any texture is needed.
 *
 * Measured on both dark surfaces this ships against, `#161616` (modal) and `#08080d` (Atlas),
 * in OKLab ΔE ×100, taking the worst of normal, protanopia and deuteranopia vision: worst pair
 * within a group 15.7, weakest consecutive-index step 43.6, every slot at or above 3.2:1
 * contrast. Separation under tritanopia is weak, as it is for any single-family palette, so
 * the label beside a swatch — not the colour — is what carries identity there.
 */
export const THREAD_PALETTE = [
  // group 1
  '#9f88ff', // periwinkle
  '#7e5e70', // plum ash
  '#504cfb', // ultramarine
  '#b5b3ba', // silver
  '#f6f4fb', // white
  // group 2
  '#7d5e72', // damson
  '#b270df', // orchid
  '#cba5b5', // dusty rose
  '#5e46f9', // indigo
  '#ffe0fb', // blossom
  // group 3
  '#3e52fc', // cobalt
  '#96949b', // ash
  '#64619b', // slate iris
  '#8688fe', // cornflower
  '#fde0fe', // petal
  // group 4
  '#7048fd', // amethyst
  '#b179eb', // heliotrope
  '#fed8ec', // shell
  '#7a5f77', // mulberry
  '#c9a5b8', // antique rose
];

/** Colours per group. Each group's five are mutually distinct without any secondary channel. */
const COLOURS_PER_VARIANT = 5;

/**
 * The secondary channel a thread is drawn with, alongside its colour: 0, 1, 2 or 3.
 *
 * Colour alone cannot carry twenty identities in one hue family (see `THREAD_PALETTE`), so
 * every surface that draws threads at a readable size pairs the colour with one of four
 * variants. Threads sharing a variant are guaranteed to differ in colour; threads whose
 * colours are close are guaranteed to differ in variant.
 *
 * What a variant *looks like* is the surface's call, because the surfaces have different room:
 * `ThreadLanes` renders 6px HTML bars and varies their stripe pattern, while the Atlas chart
 * draws SVG ribbons whose `stroke-dasharray` is already spent on the draw-in animation, so it
 * varies the station ring instead. The only contract is that a surface's four are mutually
 * distinct within that surface.
 *
 * A surface too small for any of it — the 6–8px chips and dots — is exempt: each already sits
 * beside its thread's title, so identity never rests on the swatch alone there.
 */
export type ThreadVariant = 0 | 1 | 2 | 3;

export const THREAD_VARIANT_COUNT = 4;

export const threadVariant = (index: number): ThreadVariant =>
  (Math.floor((index % THREAD_PALETTE.length) / COLOURS_PER_VARIANT) %
    THREAD_VARIANT_COUNT) as ThreadVariant;

export const threadColor = (index: number) => THREAD_PALETTE[index % THREAD_PALETTE.length];

/**
 * Scenes in narrative order (start time, falling back to id). The backend
 * guarantees every scene carries start/end, but a missing `end` would turn
 * the scene zero-width and silently empty its shots grid, dialogue, and
 * character presence — so repair rather than trust: a missing `end` borrows
 * the next scene's start (or the episode duration for the last scene).
 */
export const sortScenes = (scenes: Scene[], episodeDurationSeconds = 0): Scene[] => {
  const sorted = scenes.slice().sort((a, b) => (a.start ?? a.scene_id) - (b.start ?? b.scene_id));
  return sorted.map((scene, i) => {
    if (scene.end != null) return scene;
    const start = scene.start ?? 0;
    return { ...scene, end: sorted[i + 1]?.start ?? Math.max(episodeDurationSeconds, start) };
  });
};

export const sortThreads = (threads: NarrativeThread[]) =>
  threads.slice().sort((a, b) => a.thread_id - b.thread_id);

/**
 * Tokenize prose containing inline scene references. `[scene_3, scene_5]`
 * becomes text tokens interleaved with `{ sceneId }` tokens so the UI can
 * render clickable chips.
 */
export type SceneRefToken = { type: 'text'; text: string } | { type: 'scene'; sceneId: number };

const SCENE_REF_BLOCK_RE = /\[\s*scene[_\s]*\d+(?:\s*,\s*scene[_\s]*\d+)*\s*\]/gi;
const SCENE_ID_RE = /scene[_\s]*(\d+)/gi;

export function tokenizeSceneRefs(text: string): SceneRefToken[] {
  const tokens: SceneRefToken[] = [];
  let cursor = 0;
  for (const match of text.matchAll(SCENE_REF_BLOCK_RE)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      tokens.push({ type: 'text', text: text.slice(cursor, index) });
    }
    for (const idMatch of match[0].matchAll(SCENE_ID_RE)) {
      tokens.push({ type: 'scene', sceneId: parseInt(idMatch[1], 10) });
    }
    cursor = index + match[0].length;
  }
  if (cursor < text.length) {
    tokens.push({ type: 'text', text: text.slice(cursor) });
  }
  return tokens;
}
